import { filterOnlyPitches } from '../filters/byResultTypes.js';
import { filterOnlyHavingParameterSet } from '../filters/byParameterName.js';
import { filterOnlyMatchingTagId } from '../filters/byParameterValue.js';
import StringifiedParameterFormatter from '../formatters/StringifiedParameterFormatter.js';
import EnumValueProvider from './EnumValueProvider.js';
import buildFiltersForPitchingChart from '../filters/pitchingChartFilters.js';
import UniqueResultParametersProvider from './UniqueResultParametersProvider.js';
import * as Enums from '../enums/index.js';
import { Series, ChartSeries, BaseballResultsProcessor } from '../entities/index.js';

const buildFiltersArray = function({
    // tagging context
    pitcherId, pitchResults, pitchTypes, pitchSet, batterIds, batterHandedness,
    // scoring context
    outs, balls, strikes, runnersOn,
}) {
    return buildFiltersForPitchingChart({
        pitcherId, pitchResults, pitchTypes, pitchSet, batterIds, batterHandedness,
        outs, balls, strikes, runnersOn
    });
};

const getSeriesForPitchTracking = function(
    results, customResultsProcessor, pitchDataKey,
    {
        // tagging context
        pitcherId, pitchResults, pitchTypes, pitchSet, batterIds, batterHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {}
) {
    if (!pitchDataKey) {
        throw new TypeError('Name of pitch data parameter is required');
    }

    if (typeof pitchDataKey !== 'string') {
        throw new TypeError('Name of pitch data parameter must be an string');
    }

    let pitchParameter = EnumValueProvider.getValue(pitchDataKey, Enums.PitchData);

    if (!pitchParameter) {
        throw new Error(`Unable to determine definition for parameter ${pitchDataKey}`);
    }

    let parameterKey = pitchParameter.key;
    let extractProperty = pitchParameter.property;
    let paramParent = '';

    if (pitchParameter.parent) {
        paramParent = EnumValueProvider.getValue(pitchParameter.parent, Enums.PitchData);

        if (!paramParent){
            throw new Error(`Unable to determine definition for parent parameter ${pitchParameter.parent}`);
        }

        parameterKey = paramParent.key;
    }

    let trackingChartSeries = [];
    let resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();

    let trackingChartFilters = buildFiltersArray({
        pitcherId, pitchResults, pitchTypes, pitchSet, batterIds, batterHandedness,
        outs, balls, strikes, runnersOn,
    });

    trackingChartFilters = trackingChartFilters.concat(filterOnlyHavingParameterSet(Enums.ResultData.getDataPath(parameterKey)));

    let pitchesWithParameter =  resultsProcessor.filterData(results, trackingChartFilters);
    let resultsMapped = resultsProcessor.mapCollectionProperties(pitchesWithParameter, [
        [Enums.ResultData.Id.key],
        Enums.ResultData.getDataPath(parameterKey)
    ]);

    let categories = determinePitchCount(results, pitcherId, pitchesWithParameter, resultsProcessor);

    // TODO - move to separate method
    let dataForParameter = new Series(pitchDataKey, 2);

    for (let i = 0; i < resultsMapped.length; i++) {
        let paramValue = null;
        let category = null;

        // get value for Y
        if (paramParent && paramParent.stringifiedAs){
            let formatted = StringifiedParameterFormatter(
                resultsMapped[i][parameterKey], ';', paramParent.stringifiedAs, parseFloat
            );

            if (formatted && formatted.hasOwnProperty(extractProperty)) {
                paramValue =formatted[extractProperty];
            }
            else {
                continue;
            }
        }
        else { 
            paramValue = resultsMapped[i][parameterKey];
        }

        // get value for X
        category = categories[i];

        dataForParameter.add(resultsMapped[i][Enums.ResultData.Id.key], category, paramValue);
    }

    trackingChartSeries.push(dataForParameter);

    return new ChartSeries(trackingChartSeries, dataForParameter.getDimension(1));
};

const determinePitchCount = function(results, pitcherId, filteredResults, resultsProcessor) {
    let filtersForPitcher = filterOnlyPitches()
        .concat(filterOnlyMatchingTagId(pitcherId));

    let allPitchesInSequence = resultsProcessor.filterData(results, filtersForPitcher);
    let pitchSequence = [];

    for (let i = 0; i < allPitchesInSequence.length; i++) {
        let singlePitch = allPitchesInSequence[i];
        let singlePitchWithParam = filteredResults.find(
            r => r === singlePitch
        );

        let pitchCount = i + 1;
        if (singlePitch[Enums.ResultData.Data.key]) {
            if (
                singlePitch[Enums.ResultData.Data.key].hasOwnProperty(Enums.PitchData.AfterPlayPitchCount.key)
                && singlePitch[Enums.ResultData.Data.key][Enums.PitchData.AfterPlayPitchCount.key] > 0
            ) {
                // prefer scoring data
                pitchCount = singlePitch[Enums.ResultData.Data.key][Enums.PitchData.AfterPlayPitchCount.key];
            }
            else if (
                singlePitch[Enums.ResultData.Data.key].hasOwnProperty(Enums.PitchReprocessed.PitchCountR.key)
                && singlePitch[Enums.ResultData.Data.key][Enums.PitchReprocessed.PitchCountR.key] > 0
            ) {
                // re-processed data
                pitchCount = singlePitch[Enums.ResultData.Data.key][Enums.PitchReprocessed.PitchCountR.key];
            }
        }

        if (singlePitchWithParam && singlePitch[Enums.ResultData.Id.key] === singlePitchWithParam[Enums.ResultData.Id.key]) {
            pitchSequence.push(pitchCount);
        }
    }

    return pitchSequence;
};

const listUniqueParametersForPitchTrackingChart = function(
    results, pitchDataKey, customResultsProcessor, 
    {
        // tagging context
        pitcherId, pitchResults, pitchTypes, pitchSet, batterIds, batterHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {},
    parameters,
    combinedPitchResult
) {
    if (!pitchDataKey) {
        throw new TypeError('Name of pitch data parameter is required');
    }

    if (typeof pitchDataKey !== 'string') {
        throw new TypeError('Name of pitch data parameter must be an string');
    }

    let pitchParameter = EnumValueProvider.getValue(pitchDataKey, Enums.PitchData);

    if (!pitchParameter) {
        throw new Error(`Unable to determine definition for parameter ${pitchDataKey}`);
    }

    parameters = parameters || [
        // tagging context
        [Enums.ResultData.BatterTagId.key],
        [Enums.ResultData.TagId.key],
        Enums.ResultData.getDataPath(Enums.PitchData.Result.key),
        Enums.ResultData.getDataPath(Enums.PitchData.Classification.key),
        Enums.ResultData.getDataPath(Enums.PitchData.Mechanics.key),
        Enums.ResultData.getDataPath(Enums.PitchData.SwingHandedness.key),
        // scoring context
        Enums.ResultData.getDataPath(Enums.PitchData.BeforePlayOuts.key),
        Enums.ResultData.getDataPath(Enums.PitchData.BeforePlayBalls.key),
        Enums.ResultData.getDataPath(Enums.PitchData.BeforePlayStrikes.key),
        Enums.ResultData.getDataPath(Enums.PitchData.BeforePlayRunnersOn.key),
    ];

    const resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();

    let parameterKey = pitchParameter.key;
    let paramParent = '';

    if (pitchParameter.parent) {
        paramParent = EnumValueProvider.getValue(pitchParameter.parent, Enums.PitchData);

        if (!paramParent){
            throw new Error(`Unable to determine definition for parent parameter ${pitchParameter.parent}`);
        }

        parameterKey = paramParent.key;
    }

    let trackingChartFilters = buildFiltersArray({
        pitcherId, pitchResults, pitchTypes, pitchSet, batterIds, batterHandedness,
        outs, balls, strikes, runnersOn,
    });

    trackingChartFilters = trackingChartFilters.concat(filterOnlyHavingParameterSet(Enums.ResultData.getDataPath(parameterKey)));

    let pitchesWithParameter = resultsProcessor.filterData(results, trackingChartFilters);
    
    return UniqueResultParametersProvider(pitchesWithParameter, parameters, resultsProcessor, combinedPitchResult);
};

export default getSeriesForPitchTracking;

export {
    getSeriesForPitchTracking, listUniqueParametersForPitchTrackingChart
};